import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage, t } from '../../context/LanguageContext';
import { isWeight, gToKgStr, kgToG, fmtQty, fmtSold } from '../../utils/eventQty';

const API_URL = process.env.REACT_APP_API_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('portalToken')}` } });

const DISPOSAL_REASONS = (lang) => [
  { value: 'employee_meal',  label: t('Employee Meal',  'Comida de Empleado', lang) },
  { value: 'complimentary',  label: t('Complimentary',  'Cortesía',           lang) },
  { value: 'security',       label: t('Security',       'Seguridad',          lang) },
  { value: 'damaged',        label: t('Damaged',        'Dañado',             lang) },
  { value: 'other',          label: t('Other',          'Otro',               lang) },
];

function todayISODate() {
  return new Date().toISOString().slice(0, 10);
}

export default function PortalEventInventoryPage() {
  const { eventId } = useParams();
  const { lang } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [event, setEvent] = useState(null);
  const [day, setDay] = useState(null);
  const [date, setDate] = useState(todayISODate());

  const [inventory, setInventory] = useState({});
  const [disposals, setDisposals] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const fetchDay = async (d) => {
    setLoading(true);
    setError('');
    setSubmitSuccess(false);
    try {
      const res = await axios.get(`${API_URL}/api/portal/events/${eventId}/day?date=${d}`, auth());
      setEvent(res.data.event);
      setDay(res.data.day);
      const inv = {};
      const eps = res.data.event?.EventProducts || [];
      const epFor = (pid) => eps.find(e => e.productId === pid);
      for (const dp of res.data.day?.DayProducts || []) {
        // Sold-by-weight products are stored as grams but counted and typed in kg
        const q = (v) => (isWeight(epFor(dp.productId)) ? gToKgStr(v) : v);
        inv[dp.productId] = {
          restockQty: q(dp.restockQty ?? 0),
          endQty: dp.endQty != null ? q(dp.endQty) : '',
          posQty: dp.posQty ?? '',
        };
      }
      setInventory(inv);
      // Pre-load any existing disposals so variance calculation is immediately correct
      setDisposals((res.data.day?.Disposals || []).map(d => ({
        productId: String(d.productId),
        reason: d.reason,
        qty: isWeight(epFor(d.productId)) ? gToKgStr(d.qty) : String(d.qty),
        notes: d.notes || '',
      })));
    } catch (err) {
      if (err?.response?.status === 404) {
        setError(t(
          'No event day found for this date. Make sure the date is within the event range.',
          'No se encontró un día del evento para esta fecha. Asegúrese de que la fecha esté dentro del rango del evento.',
          lang
        ));
      } else {
        setError(err?.response?.data?.error || t('Failed to load day data.', 'Error al cargar los datos del día.', lang));
      }
      setEvent(null);
      setDay(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDay(date); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setInv = (productId, field, value) => {
    setInventory(prev => ({ ...prev, [productId]: { ...prev[productId], [field]: value } }));
  };

  const addDisposal = () => {
    const firstProduct = day?.DayProducts?.[0]?.productId;
    setDisposals(prev => [...prev, { productId: firstProduct || '', reason: 'employee_meal', qty: '', notes: '' }]);
  };

  const updateDisposal = (idx, field, value) => {
    setDisposals(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
  };

  const removeDisposal = (idx) => {
    setDisposals(prev => prev.filter((_, i) => i !== idx));
  };

  const epOf = (productId) => (event?.EventProducts || []).find(e => e.productId === parseInt(productId));
  // Typed value → stored base unit (grams for sold-by-weight products, plain units otherwise)
  const toBase = (ep, v) => (isWeight(ep) ? kgToG(v) : parseInt(v));

  // Compute sold quantity for a product: start + restock - disposals - end (grams for weight products)
  const computeSold = (productId, dp) => {
    const ep = epOf(productId);
    const inv = inventory[productId] || {};
    if (inv.endQty === '' || inv.endQty == null) return null;
    const start = dp.startQty || 0;
    const restock = toBase(ep, inv.restockQty) || 0;
    const disposed = disposals
      .filter(d => parseInt(d.productId) === parseInt(productId) && d.qty !== '')
      .reduce((s, d) => s + (toBase(ep, d.qty) || 0), 0);
    return start + restock - disposed - toBase(ep, inv.endQty);
  };

  // Variance: computed sold minus POS count (0 = perfect, non-zero = discrepancy)
  const computeVariance = (productId, dp) => {
    // POS reconciliation doesn't apply to sold-by-weight products (a cup can mix flavors)
    if (isWeight(epOf(productId))) return null;
    const inv = inventory[productId] || {};
    if (inv.posQty === '' || inv.posQty == null) return null;
    const sold = computeSold(productId, dp);
    if (sold == null) return null;
    return sold - parseInt(inv.posQty);
  };

  // Products with a non-zero variance (both endQty and posQty must be filled)
  const getDiscrepancies = () => {
    return (day?.DayProducts || []).filter(dp => {
      const variance = computeVariance(dp.productId, dp);
      return variance !== null && variance !== 0;
    });
  };

  const handleSubmit = async () => {
    // Hard block: check all products that have both end count and POS filled
    const discrepancies = getDiscrepancies();
    if (discrepancies.length > 0) {
      const names = discrepancies.map(dp => {
        const v = computeVariance(dp.productId, dp);
        const name = dp.Product?.name || `#${dp.productId}`;
        return `${name} (${v > 0 ? '+' : ''}${v})`;
      }).join(', ');
      setSubmitError(
        t(
          `Cannot submit — inventory counts don't add up for: ${names}. Check your End Count or POS Count.`,
          `No se puede enviar — los conteos no cuadran para: ${names}. Verifique el Conteo Final o el Conteo POS.`,
          lang
        )
      );
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    try {
      const products = Object.entries(inventory).map(([productId, v]) => {
        const ep = epOf(productId);
        return {
          productId: parseInt(productId),
          restockQty: toBase(ep, v.restockQty) || 0,
          endQty: v.endQty !== '' ? toBase(ep, v.endQty) : null,
          posQty: v.posQty !== '' ? parseInt(v.posQty) : null,
        };
      });

      const disposalsPayload = disposals
        .filter(d => d.productId && d.qty !== '' && d.qty > 0)
        .map(d => ({
          productId: parseInt(d.productId),
          reason: d.reason,
          qty: toBase(epOf(d.productId), d.qty),
          notes: d.notes.trim() || null,
        }));

      await axios.post(
        `${API_URL}/api/portal/events/${eventId}/day/${day.id}/submit`,
        { products, disposals: disposalsPayload },
        auth()
      );

      setSubmitSuccess(true);
      await fetchDay(date);
    } catch (err) {
      setSubmitError(err?.response?.data?.error || t('Failed to submit. Please try again.', 'Error al enviar. Inténtelo nuevamente.', lang));
    } finally {
      setSubmitting(false);
    }
  };

  const isLocked = day?.isLocked;
  const reasons = DISPOSAL_REASONS(lang);
  const discrepancies = day ? getDiscrepancies() : [];

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px 16px' }}>
      <h5 className="fw-bold mb-1">{event?.name || t('Event Inventory', 'Inventario del Evento', lang)}</h5>
      <p className="text-muted small mb-3">{t('End-of-day inventory submission', 'Envío de inventario al cierre del día', lang)}</p>

      {/* Date picker */}
      <div className="mb-3">
        <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>{t('Date', 'Fecha', lang)}</label>
        <div className="d-flex gap-2 align-items-center">
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ maxWidth: 200 }}
            value={date}
            onChange={e => { setDate(e.target.value); fetchDay(e.target.value); }}
          />
        </div>
      </div>

      {loading && <div className="text-center py-4"><span className="spinner-border spinner-border-sm" /></div>}
      {!loading && error && <div className="alert alert-warning" style={{ fontSize: 13 }}>{error}</div>}

      {!loading && day && (
        <>
          {isLocked && (
            <div className="alert alert-dark py-2 mb-3" style={{ fontSize: 13 }}>
              {t('This day is locked and cannot be edited.', 'Este día está bloqueado y no puede editarse.', lang)}
            </div>
          )}
          {day.submittedAt && !isLocked && (
            <div className="alert alert-success py-2 mb-3" style={{ fontSize: 13 }}>
              {t('Already submitted — you can update below and resubmit.', 'Ya enviado — puede actualizar a continuación y reenviar.', lang)}
            </div>
          )}
          {submitSuccess && (
            <div className="alert alert-success py-2 mb-3" style={{ fontSize: 13 }}>
              {t('Submitted successfully!', '¡Enviado con éxito!', lang)}
            </div>
          )}

          {/* Inventory table */}
          <div className="card mb-3">
            <div className="card-header py-2 fw-semibold" style={{ fontSize: 13 }}>
              {t('Inventory', 'Inventario', lang)}
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                  <thead className="table-light">
                    <tr>
                      <th>{t('Product', 'Producto', lang)}</th>
                      <th className="text-end" style={{ minWidth: 50 }}>{t('Start', 'Inicio', lang)}</th>
                      <th className="text-end" style={{ minWidth: 80 }}>{t('Restock', 'Reabast.', lang)}</th>
                      <th className="text-end" style={{ minWidth: 80 }}>{t('End Count', 'Conteo Final', lang)}</th>
                      <th className="text-end" style={{ minWidth: 80 }}>{t('POS Count', 'Conteo POS', lang)}</th>
                      <th className="text-center" style={{ minWidth: 60 }}>{t('Check', 'Verificar', lang)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(day.DayProducts || []).map(dp => {
                      const inv = inventory[dp.productId] || {};
                      const ep = epOf(dp.productId);
                      const w = isWeight(ep);
                      const sold = computeSold(dp.productId, dp);
                      const variance = computeVariance(dp.productId, dp);
                      const hasDiscrepancy = variance !== null && variance !== 0;
                      const isOk = variance === 0;

                      return (
                        <tr key={dp.productId} style={hasDiscrepancy ? { background: '#fff5f5' } : undefined}>
                          <td className="fw-semibold">{dp.Product?.name || `#${dp.productId}`}{w && <span className="text-muted fw-normal" style={{ fontSize: 11 }}> (kg)</span>}</td>
                          <td className="text-end text-muted">{fmtQty(ep, dp.startQty)}</td>
                          <td className="text-end">
                            <input
                              type="number" min="0"
                              className="form-control form-control-sm text-end"
                              style={{ width: 70, display: 'inline-block' }}
                              value={inv.restockQty ?? ''}
                              onChange={e => setInv(dp.productId, 'restockQty', e.target.value)}
                              step={w ? '0.001' : undefined}
                              disabled={isLocked}
                            />
                          </td>
                          <td className="text-end">
                            <input
                              type="number" min="0"
                              className="form-control form-control-sm text-end"
                              style={{ width: 70, display: 'inline-block', borderColor: hasDiscrepancy ? '#dc3545' : undefined }}
                              value={inv.endQty ?? ''}
                              placeholder="—"
                              onChange={e => setInv(dp.productId, 'endQty', e.target.value)}
                              step={w ? '0.001' : undefined}
                              disabled={isLocked}
                            />
                          </td>
                          <td className="text-end">
                            <input
                              type="number" min="0"
                              className="form-control form-control-sm text-end"
                              style={{ width: 70, display: 'inline-block', borderColor: hasDiscrepancy ? '#dc3545' : undefined }}
                              value={inv.posQty ?? ''}
                              placeholder="—"
                              onChange={e => setInv(dp.productId, 'posQty', e.target.value)}
                              disabled={isLocked}
                            />
                          </td>
                          <td className="text-center align-middle">
                            {isOk && (
                              <span title={`Sold: ${sold}`} style={{ color: '#198754', fontSize: 16 }}>✓</span>
                            )}
                            {hasDiscrepancy && (
                              <span
                                className="fw-bold"
                                style={{ color: '#dc3545', fontSize: 12, whiteSpace: 'nowrap' }}
                                title={t(`Sold ${sold}, POS ${parseInt(inv.posQty)} — off by ${Math.abs(variance)}`, `Vendido ${sold}, POS ${parseInt(inv.posQty)} — diferencia de ${Math.abs(variance)}`, lang)}
                              >
                                {variance > 0 ? '+' : ''}{variance}
                              </span>
                            )}
                            {variance === null && sold !== null && (
                              <span className="text-muted" style={{ fontSize: 11 }}>{t('Sold', 'Vend.', lang)}: {fmtSold(ep, sold)}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Discrepancy explanation legend */}
              <div className="px-3 pb-2 pt-1" style={{ fontSize: 11, color: '#6c757d' }}>
                {t(
                  'Check column = Sold (start + restock − disposals − end) minus POS count. Must be 0 to submit.',
                  'Columna Verificar = Vendido (inicio + reabast. − bajas − final) menos POS. Debe ser 0 para enviar.',
                  lang
                )}
              </div>
            </div>
          </div>

          {/* Discrepancy warning banner */}
          {discrepancies.length > 0 && (
            <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>
              <strong>{t('Discrepancy detected', 'Discrepancia detectada', lang)}</strong>
              {' — '}
              {t(
                'The numbers don\'t add up for the highlighted products. Correct your End Count or POS Count before submitting.',
                'Los números no cuadran en los productos resaltados. Corrija el Conteo Final o el Conteo POS antes de enviar.',
                lang
              )}
            </div>
          )}

          {/* Disposals */}
          <div className="card mb-4">
            <div className="card-header d-flex align-items-center justify-content-between py-2">
              <span className="fw-semibold" style={{ fontSize: 13 }}>
                {t('Free / Non-sale disposals', 'Bajas / Descargos sin venta', lang)}
              </span>
              {!isLocked && (
                <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 11 }} onClick={addDisposal}>
                  + {t('Add', 'Agregar', lang)}
                </button>
              )}
            </div>
            <div className="card-body">
              {disposals.length === 0 && (
                <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                  {t(
                    'No disposals for this day. Add entries for employee meals, complimentary items, security, damaged goods, etc.',
                    'Sin bajas para este día. Agregue entradas para comidas de empleados, cortesías, seguridad, bienes dañados, etc.',
                    lang
                  )}
                </p>
              )}
              {disposals.map((d, idx) => (
                <div key={idx} className="row g-2 align-items-end mb-2">
                  <div className="col-4">
                    <label className="form-label mb-1" style={{ fontSize: 11 }}>{t('Product', 'Producto', lang)}</label>
                    <select className="form-select form-select-sm" value={d.productId} onChange={e => updateDisposal(idx, 'productId', e.target.value)} disabled={isLocked}>
                      <option value="">{t('Select…', 'Seleccionar…', lang)}</option>
                      {(day.DayProducts || []).map(dp => (
                        <option key={dp.productId} value={dp.productId}>{dp.Product?.name || `#${dp.productId}`}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-3">
                    <label className="form-label mb-1" style={{ fontSize: 11 }}>{t('Reason', 'Motivo', lang)}</label>
                    <select className="form-select form-select-sm" value={d.reason} onChange={e => updateDisposal(idx, 'reason', e.target.value)} disabled={isLocked}>
                      {reasons.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div className="col-2">
                    <label className="form-label mb-1" style={{ fontSize: 11 }}>{isWeight(epOf(d.productId)) ? t('Qty (kg)', 'Cant. (kg)', lang) : t('Qty', 'Cant.', lang)}</label>
                    <input type="number" min={isWeight(epOf(d.productId)) ? '0.001' : '1'} step={isWeight(epOf(d.productId)) ? '0.001' : undefined} className="form-control form-control-sm" value={d.qty} onChange={e => updateDisposal(idx, 'qty', e.target.value)} disabled={isLocked} />
                  </div>
                  <div className="col">
                    <label className="form-label mb-1" style={{ fontSize: 11 }}>{t('Notes', 'Notas', lang)}</label>
                    <input type="text" className="form-control form-control-sm" value={d.notes} onChange={e => updateDisposal(idx, 'notes', e.target.value)} placeholder={t('optional', 'opcional', lang)} disabled={isLocked} />
                  </div>
                  {!isLocked && (
                    <div className="col-auto pb-1">
                      <button className="btn btn-link btn-sm text-danger p-0" onClick={() => removeDisposal(idx)}>✕</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {submitError && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{submitError}</div>}

          {!isLocked && (
            <div className="d-grid">
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={submitting || discrepancies.length > 0}
                title={discrepancies.length > 0 ? t('Fix discrepancies before submitting', 'Corrija las discrepancias antes de enviar', lang) : undefined}
              >
                {submitting
                  ? t('Submitting…', 'Enviando…', lang)
                  : day.submittedAt
                    ? t('Update Submission', 'Actualizar Envío', lang)
                    : t('Submit End-of-Day Inventory', 'Enviar Inventario de Cierre', lang)
                }
              </button>
              {discrepancies.length > 0 && (
                <div className="text-center mt-2 text-danger" style={{ fontSize: 12 }}>
                  {t(
                    `${discrepancies.length} product${discrepancies.length !== 1 ? 's' : ''} with discrepancy — fix before submitting`,
                    `${discrepancies.length} producto${discrepancies.length !== 1 ? 's' : ''} con discrepancia — corrija antes de enviar`,
                    lang
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
